// #336 slice 5, brief §5.3 «Ancora»: the load a round at effort or at % of the
// MAX comes to. Shown read only beside the round («rif. ≈ X kg»), and the start
// of a load rule on a row that had no load. The arithmetic is the logger's own
// chain (resolveDefaultWeight), fed the MAX only. Never written into a cell.
// Keep free of React and any UI import.
import { rpeToRir } from "./effortScale.js";
import type { SlotDraft } from "./programEdit.js";
import { readRound, type ReadSet } from "./programProgression.js";
import { resolveDefaultWeight, type DefaultWeightContext, type MaxForProposal } from "./resolveDefaultWeight.js";
import { roundLoadable } from "./roundLoadable.js";
import type { UnitsPreference } from "./schemas/profile.js";
import { fromCanonicalKg } from "./units.js";

/** One set's load in kg from `max`; null when the chain declines. */
const setLoadKg = (set: ReadSet, max: MaxForProposal): number | null => {
  if (set.value === null) return null;
  const ctx: DefaultWeightContext = {
    userMax: max.valueKg,
    lastLogged: undefined,
    ...(max.source !== undefined ? { userMaxSource: max.source } : {}),
    ...(max.setAt !== undefined ? { userMaxSetAt: max.setAt } : {}),
  };
  let r;
  if (set.kind === "percent-of-max") {
    r = resolveDefaultWeight({ kind: "percentage", pctOfMax: set.value }, ctx);
  } else if ((set.kind === "rpe" || set.kind === "rir") && set.reps !== null) {
    const targetRir = set.kind === "rir" ? set.value : rpeToRir(set.value);
    r = resolveDefaultWeight({ kind: "none" }, { ...ctx, effort: { targetRir, reps: set.reps } });
  } else return null;
  return r.source === "blank" ? null : r.kg;
};

/** Round `w`'s reference load in kg, its heaviest set's. null: no MAX, a
 *  timed row, a round with no set at effort or at %, or not run there. */
export const roundReferenceKg = (slot: SlotDraft, w: number, max: MaxForProposal | undefined): number | null => {
  if (!max || slot.setRows[0]?.workKind === "countdown") return null;
  const kgs = (readRound(slot, w, false) ?? []).flatMap((s) => {
    const kg = setLoadKg(s, max);
    return kg === null ? [] : [kg];
  });
  return kgs.length > 0 ? Math.max(...kgs) : null;
};

/** A reference in kg as the logger proposes it, in `unit`: floored to a
 *  loadable weight by roundLoadable (default plates), so «rif. ≈ X» is the
 *  load the logger seeds. */
export const referenceLoadable = (kg: number, unit: UnitsPreference): number =>
  fromCanonicalKg(roundLoadable(kg, unit), unit);
